import { reactive, ref, computed } from 'vue'
import { useRoute } from '@/composables/common/router/useRouter'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'

/**
 * Composable para el formulario de Valoración (técnica, táctica, física,
 * psicológica, personalidad) usado dentro del modal Editar/Crear de
 * Valoración en el detalle del deportista.
 *
 * @param {Object}   options
 * @param {String|Number} options.base_id   ID del base (deportista) que se está valorando
 * @param {Object}   options.valoracion     Valoración existente (null si es creación)
 * @returns {{
 *   form:    Object,
 *   loading: import('vue').Ref<boolean>,
 *   submit:  (isValid: boolean) => Promise<boolean>
 * }}
 */
export function useValoracionForm({ base_id, valoracion }) {
    const toast = useToast()
    const route = useRoute()
    const loading = ref(false)

    const form = reactive({
        tecnica: '',
        tactica: '',
        fisica: '',
        psicologica: '',
        personalidad: ''
    })

    if (valoracion) {
        form.tecnica = valoracion.tecnica ?? ''
        form.tactica = valoracion.tactica ?? ''
        form.fisica = valoracion.fisica ?? ''
        form.psicologica = valoracion.psicologica ?? ''
        form.personalidad = valoracion.personalidad ?? ''
    }

    const lista_id = computed(() => Number(route.params.lista_id))

    const submit = async isValid => {
        if (!isValid) return false

        loading.value = true
        try {
            const alias = valoracion
                ? 'back.bases.valoraciones.update'
                : 'back.bases.valoraciones.crear'
            const payload = {
                filters: {},
                base_id,
                valoracion_id: valoracion ? valoracion.id : 0,
                lista_id: lista_id.value,
                ...form
            }
            const res = await store.dispatch(config.getVuexAlias(alias), payload)
            toast.success(res.data.message || 'Valoración guardada correctamente.')
            return true
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Ha ocurrido un error al guardar la valoración.')
            return false
        } finally {
            loading.value = false
        }
    }

    return {
        form,
        loading,
        submit
    }
}
